'use client'

import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api-fetch'
import { queryKeys } from '../keys'
import type { LitePptProjectDTO, LitePptSettingsDTO } from '@/types/lite-ppt'

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = typeof data?.message === 'string'
      ? data.message
      : typeof data?.error === 'string'
        ? data.error
        : 'Request failed'
    throw new Error(message)
  }
  return data as T
}

export function useLitePptSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.litePpt.settings(),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    queryFn: async (): Promise<{ settings: LitePptSettingsDTO }> => {
      const res = await apiFetch('/api/lite-ppt/settings')
      return jsonOrThrow<{ settings: LitePptSettingsDTO }>(res)
    },
  })
}

export function useLitePptProject(projectId?: string | null) {
  return useQuery({
    queryKey: queryKeys.litePpt.project(projectId || ''),
    enabled: !!projectId,
    staleTime: 5_000,
    refetchInterval: (query) => query.state.data?.project.status === 'generating' ? 2500 : false,
    queryFn: async (): Promise<{ project: LitePptProjectDTO }> => {
      const res = await apiFetch(`/api/lite-ppt/projects/${projectId}`)
      return jsonOrThrow<{ project: LitePptProjectDTO }>(res)
    },
  })
}
